import {
  Box,
  buildPath,
  buildTemplatePath,
  Card,
  Grid,
  IconButton,
  Page,
  PageRoute,
  Stack,
  Tooltip,
  Typography,
  useElementBreakpoints,
} from "@compass/core";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import AutoStoriesSharpIcon from '@mui/icons-material/AutoStoriesSharp';
import { Home } from "generated/openApi";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { userManualPageRoute } from "../UserManual/UserManualPage";
import { DashboardGrid, SortableCard } from "./components/DashboardGrid";
import {
    ServiceChartCard,
    ServiceTableCard,
} from "./ServiceCards";
import {
    MatchChartCard,
    MatchTableCard,
} from "./MatchCards";
import { ManageButton } from "./components/ManageButton";
import { CardConfigurationDrawer } from "./components";
import { DashboardCardConfig, CardId } from "./types";
import { DEFAULT_CARD_CONFIGS } from "./config";
import { NotificationsCard } from "./NotificationsDashboard";
import { TotalStudentsCard } from "./StudentDashboard";
import { SchoolEnrollmentCard } from "./SchoolEnrollmentDashboard";
import { CardConfigService } from "./services";

const routeName = "home";

export const homePageRoute: PageRoute = {
  handle: "Home",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const HomePage = () => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const navigate = useNavigate();
  
  const [home, setHome] = useState<Home>({} as Home);
  const [cardConfigs, setCardConfigs] = useState<DashboardCardConfig[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Derive visible cards - automatically updates when dependencies change
  const visibleCards = useMemo(
    () => CardConfigService.getVisibleCards(cardConfigs, home.dashboards),
    [cardConfigs, home.dashboards]
  );

  // Derive authorized cards for drawer
  const authorizedCards = useMemo(
    () => CardConfigService.filterByAuthorization(cardConfigs, home.dashboards),
    [cardConfigs, home.dashboards]
  );

  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    ApiService.home.get().then(setHome);
  }, []);

  useEffect(() => {
    const cards = CardConfigService.initialize();
    setCardConfigs(cards.sort((a, b) => a.order - b.order));
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const activeId = active.id as CardId;
  const overId = over.id as CardId;

  setCardConfigs((prev) => {
    //Visible cards for this user (authorized + enabled + sorted)
    const visible = CardConfigService.getVisibleCards(prev, home.dashboards);
    const visibleIds: CardId[] = visible.map((c) => c.id);

    const oldIndex = visibleIds.indexOf(activeId);
    const newIndex = visibleIds.indexOf(overId);

    if (oldIndex === -1 || newIndex === -1) {
      return prev;
    }

    //Reorder only visible cards
    const reorderedVisibleIds = arrayMove<CardId>(visibleIds, oldIndex, newIndex);
    const visibleIdSet = new Set<CardId>(reorderedVisibleIds);

    //Build new ordered list
    const reorderedAll: DashboardCardConfig[] = [
      ...reorderedVisibleIds.map((id) => {
        const match = prev.find((card) => card.id === id);
        if (!match) {
          // Should never happen if ids are consistent
          console.warn("No card config found for id:", id);
        }
        return match as DashboardCardConfig;
      }),
      ...prev
        .filter((card) => !visibleIdSet.has(card.id))
        .sort((a, b) => a.order - b.order),
    ];

    //normalize order so it's unique
    const updated = reorderedAll.map((card, index) => ({
      ...card,
      order: index + 1,
    }));

    CardConfigService.save(updated);
    return updated;
  });
};

  const handleManageClick = () => {
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleToggleCard = (cardId: string) => {
    setCardConfigs((prev) => {
      const updated = CardConfigService.toggleCard(prev, cardId);
      CardConfigService.save(updated);
      return updated;
    });
  };

  return (
    <Page>
      <Stack direction="column" spacing="lg" sx={{ width: '100%' }}>
        <Card>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="column" spacing="sm">
              <Typography variant="h4">Welcome, {home?.userFirstName}</Typography>
            </Stack>
            <Stack direction="row" spacing="md" alignItems="center">
              <ManageButton onClick={handleManageClick} />
              <Box>
                <Tooltip title={<Box>User Manual</Box>}>
                  <IconButton 
                    onClick={() => navigate(`/${userManualPageRoute.getPath()}`)}
                    aria-label="Open User Manual"
                  >
                    <AutoStoriesSharpIcon sx={{ fontSize: 50 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Stack>
          </Stack>
        </Card>
        
        <Box sx={{ width: "100%" }}>
          <DndContext 
            sensors={sensors} 
            collisionDetection={closestCenter} 
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={visibleCards.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <DashboardGrid>
                {visibleCards.map((card) => (
                  <Grid 
                    key={card.id} 
                    item 
                    xs={setBreakpoints({ md: 4, xs: 12 })} 
                    sx={{ display: "flex" }}
                  >
                    <SortableCard id={card.id}>
                      {card.id === "service-chart" && <ServiceChartCard />}
                      {card.id === "service-table" && <ServiceTableCard />}
                      {card.id === "match-chart" && <MatchChartCard />}
                      {card.id === "match-table" && <MatchTableCard />}
                      {card.id === "notifications" && <NotificationsCard />}
                      {card.id === "total-students" && <TotalStudentsCard />}
                      {card.id === "school-enrollment" && <SchoolEnrollmentCard />}
                    </SortableCard>
                  </Grid>
                ))}
              </DashboardGrid>
            </SortableContext>
          </DndContext>
        </Box>
      </Stack>
      
      <CardConfigurationDrawer
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        cards={authorizedCards}
        onToggleCard={handleToggleCard}
      />
    </Page>
  );
};